'use client';
import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import { Sparkles, Layout, Search, Image, FileText, Palette, Tag, Share2, ShoppingBag } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const ICON_MAP: Record<string, React.ElementType> = {
  image: Image,
  share2: Share2,
  shoppingBag: ShoppingBag,
  palette: Palette,
  fileText: FileText,
};

function formatCount(template: string, count: number): string {
  return template.replace('{count}', String(count));
}

export default function TemplatesContent() {
  const { t } = useLanguage();
  const tp = t?.templatesPage;
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedPrompt, setExpandedPrompt] = useState<string | null>(null);

  const categoryKeys = tp?.categoryKeys ?? [];
  const items = tp?.items ?? [];

  const filterTabs = useMemo(
    () => [{ key: 'all', label: tp?.categoryAll ?? 'All' }, ...categoryKeys.map((key) => ({
      key,
      label: tp?.categories?.[key as keyof typeof tp.categories]?.label ?? key,
    }))],
    [tp, categoryKeys]
  );

  const filtered = items.filter((item) => {
    const matchCat = activeCategory === 'all' || item.categoryKey === activeCategory;
    const haystack = `${item.title} ${item.desc} ${item.tags.join(' ')}`.toLowerCase();
    const matchSearch = search === '' || haystack.includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <main className="min-h-screen bg-background pt-24 pb-20">
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-12">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Layout size={13} className="text-primary" />
            <span className="text-xs font-600 text-primary">{tp?.badge ?? 'Templates'}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-800 text-foreground mb-5 leading-tight">
            {tp?.titleBefore ?? 'Ready-to-Use'}{' '}
            <span className="gradient-text">{tp?.titleHighlight ?? 'AI Templates'}</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed mb-6">
            {tp?.subtitle}
          </p>
          <div className="relative">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder={tp?.searchPlaceholder ?? 'Search templates...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-muted border border-border rounded-xl pl-10 pr-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
            />
          </div>
        </div>
      </section>

      {activeCategory === 'all' && search === '' && (
        <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-10">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {categoryKeys.map((key) => {
              const cat = tp?.categories?.[key as keyof typeof tp.categories];
              const count = items.filter((item) => item.categoryKey === key).length;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveCategory(key)}
                  className="glass-card rounded-xl p-4 border border-border text-left hover:border-primary/30 transition-colors"
                >
                  <p className="text-xs font-700 text-foreground mb-1">{cat?.label ?? key}</p>
                  <p className="text-xs text-muted-foreground mb-2">{cat?.description}</p>
                  <p className="text-xs text-primary font-600">
                    {formatCount(tp?.templatesCount ?? '{count} templates', count)}
                  </p>
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-8">
        <div className="flex flex-wrap gap-2">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveCategory(tab.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-500 border transition-colors ${
                activeCategory === tab.key
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-muted border-border text-muted-foreground hover:text-foreground hover:border-primary/30'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-16">
        {filtered.length === 0 ? (
          <p className="text-muted-foreground text-sm">{tp?.empty}</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((tmpl) => {
              const TmplIcon = ICON_MAP[tmpl.icon] || Image;
              const categoryLabel =
                tp?.categories?.[tmpl.categoryKey as keyof typeof tp.categories]?.label ??
                tmpl.categoryKey;
              const expandKey = tmpl.id;
              const isExpanded = expandedPrompt === expandKey;
              return (
                <div key={tmpl.id} className="glass-card rounded-xl p-5 border border-border flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <TmplIcon size={15} className="text-primary" />
                    </div>
                    <span className="text-xs font-600 uppercase tracking-widest text-muted-foreground">
                      {categoryLabel}
                    </span>
                  </div>
                  <h3 className="text-sm font-700 text-foreground mb-1.5">{tmpl.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed flex-1 mb-3">{tmpl.desc}</p>
                  {isExpanded && (
                    <div className="mb-3 p-3 rounded-lg bg-muted/50 border border-border/50">
                      <p className="text-xs font-600 text-muted-foreground mb-1.5">{tp?.promptLabel}</p>
                      <p className="text-xs text-foreground leading-relaxed font-mono">{tmpl.prompt}</p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {tmpl.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted border border-border text-xs text-muted-foreground"
                      >
                        <Tag size={9} />
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-auto">
                    <button
                      type="button"
                      onClick={() => setExpandedPrompt(isExpanded ? null : expandKey)}
                      className="btn-secondary px-3 py-2 rounded-lg text-xs flex-1 text-center"
                    >
                      {isExpanded ? tp?.hidePrompt : tp?.viewPrompt}
                    </button>
                    <Link
                      href="/sign-up-login-screen"
                      className="btn-primary px-3 py-2 rounded-lg text-xs flex-1 text-center"
                    >
                      {tp?.useTemplate}
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="rounded-2xl bg-muted/50 border border-border p-8 text-center">
          <h2 className="text-xl font-700 text-foreground mb-2">{tp?.ctaTitle}</h2>
          <p className="text-muted-foreground text-sm mb-6">{tp?.ctaDesc}</p>
          <Link
            href="/sign-up-login-screen"
            className="btn-primary px-6 py-2.5 rounded-xl text-sm inline-flex items-center gap-2"
          >
            <Sparkles size={14} /> {tp?.ctaButton}
          </Link>
        </div>
      </section>
    </main>
  );
}
